// admin.js

import React, { useEffect, useState, useRef } from "react";
import * as XLSX from 'xlsx';
import {
  Spin,
  Row,
  Col,
  Card,
  Table,
  Input,
  Button,
  Tag,
  Avatar,
  Tooltip,
  message,
  Segmented,
} from "antd";
import { SearchOutlined, FileExcelOutlined, ClockCircleOutlined, UserOutlined, HolderOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { API_CONFIG } from "./config";
import { useAuth } from "../AuthContext";
import { apiFetch } from "../utils/apiFetch";

import { Modal, Form, Switch, Select, Grid } from "antd";

const { Search } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

const roles = [
  { id: 1, name: "Lead Manager" },
  { id: 2, name: "Care Coordinator" },
  { id: 3, name: "Verification Officer" },
  { id: 4, name: "Tutor Supervisor" },
  { id: 5, name: "Super Admin" },
];

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatDateSA(input){
  if(!input) return "-";
  const d = new Date(input);
  if(isNaN(d.getTime())) return String(input);
  const day = d.getDate();
  const mon = MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2,'0');
  const mm = String(d.getMinutes()).padStart(2,'0');
  return `${day} ${mon} ${year}, ${hh}:${mm}`;
}

function initialChar(name, username){
  const s = (name && String(name).trim()) || (username && String(username).trim()) || "?";
  return s.charAt(0).toUpperCase();
}

// Friendly relative time (South Africa UTC+2)
function timeAgoSA(input){
  if(!input) return '-';
  const d = new Date(input);
  if(isNaN(d.getTime())) return String(input);
  const now = new Date();
  const diff = Math.max(0, (now.getTime() - d.getTime())/1000);
  if(diff < 60) return `${Math.floor(diff)}s ago`;
  if(diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if(diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  const days = Math.floor(diff/86400);
  if(days === 1) return 'yesterday';
  return `${days}d ago`;
}
// Map ResultType to a tone class
function toneForType(t){
  const s = (t||'').toLowerCase();
  if(s.includes('fail') || s.includes('error')) return 'err';
  if(s.includes('warn')) return 'warn';
  if(s.includes('success') || s.includes('ok') || s.includes('pass')) return 'ok';
  return 'info';
}

// Top-N entries utility
function topEntries(obj, limit = 3){
  if(!obj) return [];
  return Object.entries(obj).sort((a,b)=> b[1]-a[1]).slice(0, limit);
}

// ——— Analytics helpers ———
function getUserLogsFor(user, logs){
  if(!user) return [];
  const uid = user.UserID;
  const uname = (user.Username || '').toLowerCase();
  return logs.filter(l => (uid && l.UserID === uid) || (uname && (l.Username || '').toLowerCase() === uname));
}
function makeBuckets(logs, days = 14){
  const out = [];
  const now = new Date();
  for(let i = days - 1; i >= 0; i--){
    const d = new Date(now);
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0,10);
    out.push({ key, label: `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`, count: 0, dow: d.getDay() });
  }
  logs.forEach(l => {
    const dt = new Date(l.ActionTime);
    if(isNaN(dt.getTime())) return;
    dt.setHours(0,0,0,0);
    const key = dt.toISOString().slice(0,10);
    const bucket = out.find(b => b.key === key);
    if(bucket) bucket.count += 1;
  });
  return out;
}
function sparkPathFromCounts(counts, width, height){
  const max = Math.max(1, ...counts);
  const step = counts.length > 1 ? (width / (counts.length - 1)) : width;
  let d = '';
  counts.forEach((c, i) => {
    const x = i * step;
    const y = height - (c / max) * height;
    d += (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`);
  });
  return d;
}

export default function Admin() {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [logData, setLogData] = useState([]);
  const [selectedUserLogs, setSelectedUserLogs] = useState([]);
  const [logSearch, setLogSearch] = useState("");
  // --- Mobile logs filter and limit state
  const [logFilter, setLogFilter] = useState('all');
  const [mobileLogLimit, setMobileLogLimit] = useState(12);
  // --- View toggles for users/logs
  const [viewUsers, setViewUsers] = useState('cards'); // 'cards' | 'table' (desktop only)
  const [viewLogs, setViewLogs] = useState('cards');   // 'cards' | 'table' (desktop only)
  const [focusActivity, setFocusActivity] = useState(false);

  // Sort helper: latest activity first
  const sortByLastLoginDesc = (a, b) => {
    const ta = a?.LastLoginDate ? new Date(a.LastLoginDate).getTime() : 0;
    const tb = b?.LastLoginDate ? new Date(b.LastLoginDate).getTime() : 0;
    return tb - ta; // newer first
  };
  // Keep a live, sorted view of filtered users
  const sortedByLastLogin = React.useMemo(
    () => [...filteredData].sort(sortByLastLoginDesc),
    [filteredData]
  );

  // --- Drag & drop ordering for User Data cards (desktop) ---
  const keyForUser = (u) => (u && (u.UserID != null ? `id:${u.UserID}` : (u.Username ? `u:${u.Username}` : 'temp')));
  const [userOrder, setUserOrder] = useState([]);
  const [dragUserId, setDragUserId] = useState(null);
  const [overUserId, setOverUserId] = useState(null);

  // Load saved order once
  useEffect(() => {
    try {
      const raw = localStorage.getItem('adminUserCardOrder');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) setUserOrder(arr);
      }
    } catch (e) {}
  }, []);

  // Ensure all visible users exist in the order array (append any new/missing)
  useEffect(() => {
    const ids = sortedByLastLogin.map(keyForUser);
    setUserOrder((prev) => {
      const base = Array.isArray(prev) && prev.length ? prev : ids;
      const existing = base.filter((id) => ids.includes(id));
      const missing = ids.filter((id) => !existing.includes(id));
      const merged = [...existing, ...missing];
      if (JSON.stringify(merged) !== JSON.stringify(prev)) {
        try { localStorage.setItem('adminUserCardOrder', JSON.stringify(merged)); } catch (e) {}
        return merged;
      }
      return prev;
    });
  }, [sortedByLastLogin]);

  const orderedUsers = React.useMemo(() => {
    const ids = (userOrder && userOrder.length) ? userOrder : sortedByLastLogin.map(keyForUser);
    const idx = new Map(ids.map((id, i) => [id, i]));
    return [...filteredData].sort((a, b) => (idx.get(keyForUser(a)) ?? 1e9) - (idx.get(keyForUser(b)) ?? 1e9));
  }, [filteredData, userOrder, sortedByLastLogin]);

  const onCardDragStart = (id) => (e) => { if (isMobile) return; setDragUserId(id); e.dataTransfer.effectAllowed = 'move'; };
  const onCardDragOver  = (id) => (e) => { if (isMobile) return; e.preventDefault(); setOverUserId(id); };
  const onCardDrop      = (id) => (e) => {
    if (isMobile) return; e.preventDefault(); setOverUserId(null);
    if (!dragUserId || dragUserId === id) { setDragUserId(null); return; }
    setUserOrder((prev) => {
      const arr = (prev && prev.length) ? [...prev] : filteredData.map(keyForUser);
      const from = arr.indexOf(dragUserId);
      const to = arr.indexOf(id);
      if (from === -1 || to === -1) return prev;
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      try { localStorage.setItem('adminUserCardOrder', JSON.stringify(arr)); } catch (e) {}
      return arr;
    });
    setDragUserId(null);
  };
  const onCardDragEnd   = () => { setDragUserId(null); setOverUserId(null); };

  const navigate = useNavigate();
  const { user } = useAuth();
const screens = useBreakpoint();
const isMobile = !screens.md;
const isVerySmall = !screens.sm;
const [pageSize, setPageSize] = useState(10);
useEffect(() => {
  const calc = () => {
    const vh = window.innerHeight || 800;
    const reserved = 420; // header, filters, cards
    const rowH = 40; // fixed approximate row height
    const ps = Math.max(8, Math.min(25, Math.floor((vh - reserved) / rowH)));
    setPageSize(ps);
  };
  calc();
  window.addEventListener('resize', calc);
  return () => window.removeEventListener('resize', calc);
}, []);

// Internal scroll height for big screens
const [scrollY, setScrollY] = useState(undefined);
useEffect(() => {
  const calcY = () => {
    const vh = window.innerHeight || 800;
    // Reserve space for navbar, Overview card, toolbars, and gaps
    const reservedTop = isMobile ? 520 : 420;
    const y = Math.max(260, vh - reservedTop);
    setScrollY(y);
  };
  calcY();
  window.addEventListener('resize', calcY);
  return () => window.removeEventListener('resize', calcY);
}, [isMobile]);
// Reset mobile log limit on filter/search/isMobile change
useEffect(() => { setMobileLogLimit(12); }, [logFilter, logSearch, isMobile]);

  // --- Analytics chart user state/effects ---
  const [chartUserId, setChartUserId] = useState(null);
  useEffect(() => {
    if(!chartUserId && tableData && tableData.length){
      setChartUserId(tableData[0].UserID);
    }
  }, [tableData, chartUserId]);
  useEffect(() => {
    if(selectedPerson && selectedPerson.UserID){
      setChartUserId(selectedPerson.UserID);
    }
  }, [selectedPerson]);
  // --- Sparkline hover state ---
  const [sparkHoverIdx, setSparkHoverIdx] = useState(null);

  // Theme toggle: 'dark' or 'light' with localStorage persistence
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('adminTheme') || 'dark'; } catch (e) { return 'dark'; }
  });
  useEffect(() => {
    try { localStorage.setItem('adminTheme', theme); } catch (e) {}
  }, [theme]);

  const formData = {
    username: user.username,
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiFetch(API_CONFIG.getUsersUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Ocp-Apim-Subscription-Key": API_CONFIG.apiKey,
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          message.error("Failed to load user data from the server.");
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const users = Array.isArray(data?.rtv?.ResultSets?.Table1)
          ? data.rtv.ResultSets.Table1
          : [];
        const logs = Array.isArray(data?.rtv?.ResultSets?.Table2)
          ? data.rtv.ResultSets.Table2
          : [];

        setTableData(users);
        setFilteredData(users);
        setLogData(logs);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = (value) => {
    const searchValue = value ? value.toLowerCase() : "";
    const filtered = tableData.filter((item) => {
      const username = item.Username?.toLowerCase() || "";
      const email = item.Email?.toLowerCase() || "";
      const fullName = item.FullName?.toLowerCase() || "";
      return (
        username.includes(searchValue) ||
        email.includes(searchValue) ||
        fullName.includes(searchValue)
      );
    });
    setFilteredData(filtered);
  };

  const filteredLogData = logData.filter((log) => {
    const search = logSearch.toLowerCase();
    return (
      log.Username?.toLowerCase().includes(search) ||
      log.ResultType?.toLowerCase().includes(search) ||
      log.Action?.toLowerCase().includes(search) ||
      log.PerformedBy?.toLowerCase().includes(search)
    );
  });
  const sortedLogDataDesc = React.useMemo(() => {
    const ts = (x) => (x?.ActionTime ? new Date(x.ActionTime).getTime() : 0);
    return [...filteredLogData].sort((a, b) => ts(b) - ts(a));
  }, [filteredLogData]);
  // Mobile logs derived list (applies quick filters)
  const mobileLogs = filteredLogData.filter((l) => {
    if (logFilter === 'today') {
      const d = new Date(l.ActionTime);
      const start = new Date(); start.setHours(0,0,0,0);
      const end = new Date(start); end.setDate(end.getDate()+1);
      return d >= start && d < end;
    }
    if (logFilter === 'success') return toneForType(l.ResultType) === 'ok';
    if (logFilter === 'errors') return toneForType(l.ResultType) === 'err';
    return true; // 'all'
  });
  const mobileLogsToShow = mobileLogs.slice(0, mobileLogLimit);

  // Group mobile logs by Username with counts by type, action, and details
  const mobileGroups = React.useMemo(() => {
    const map = {};
    mobileLogs.forEach((l) => {
      const uname = l.Username || 'Unknown';
      if(!map[uname]){
        map[uname] = { username: uname, total: 0, types:{}, actions:{}, details:{}, last: null, performers: new Set() };
      }
      const g = map[uname];
      g.total += 1;
      const type = l.ResultType || 'Info';
      g.types[type] = (g.types[type]||0) + 1;
      const act = l.Action || '—';
      g.actions[act] = (g.actions[act]||0) + 1;
      const det = l.Details ? String(l.Details) : '—';
      g.details[det] = (g.details[det]||0) + 1;
      const t = new Date(l.ActionTime);
      if(!g.last || (new Date(g.last) < t)) g.last = l.ActionTime;
      if(l.PerformedBy) g.performers.add(l.PerformedBy);
    });
    const arr = Object.values(map).map(g => ({
      ...g,
      performers: Array.from(g.performers || [])
    }));
    // Sort by most activity in window
    arr.sort((a,b)=> b.total - a.total);
    return arr;
  }, [mobileLogs]);
  const mobileGroupsToShow = mobileGroups.slice(0, mobileLogLimit);

  const handleRowClick = (record) => {
    const userLogs = logData.filter(log => log.UserID === record.UserID);
    setSelectedPerson(record);
    setSelectedUserLogs(userLogs);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handleAddClick = () => {
    setSelectedPerson({
      UserID: undefined,
      Username: "",
      Email: "",
      FullName: "",
      IsActive: true,
      RoleId: "",
      FailedLoginAttempts: 0,
      LastLoginDate: "",
      PasswordLastChanged: "",
      RequiresPasswordChange: true,
    });
    setIsModalVisible(true);
  };

  // Handler for saving user from popup form
  const handleSavePerson = async (updatedUser) => {
    try {
      const response = await apiFetch(API_CONFIG.saveUserUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Ocp-Apim-Subscription-Key": API_CONFIG.apiKey,
        },
        body: JSON.stringify(updatedUser),
      });

      // Logging the full API response object
      console.log("Save API response:", response);
      // Optionally log the JSON body as well
      const responseBody = await response.json().catch(() => null);
      console.log("Save API response body:", responseBody);

      if (response.status === 200) {
        // Use API response if it returns the full user, else use updatedUser
        const savedUser = responseBody || updatedUser;

        setTableData((prev) => {
          // If UserID exists, update, else add as new (prevent duplicate Username)
          if (savedUser.UserID) {
            return prev.map((user) =>
              user.UserID === savedUser.UserID ? savedUser : user
            );
          } else if (!prev.find((u) => u.Username === savedUser.Username)) {
            return [...prev, savedUser];
          } else {
            // Prevent duplicates by Username
            return prev;
          }
        });
        setFilteredData((prev) => {
          if (savedUser.UserID) {
            return prev.map((user) =>
              user.UserID === savedUser.UserID ? savedUser : user
            );
          } else if (!prev.find((u) => u.Username === savedUser.Username)) {
            return [...prev, savedUser];
          } else {
            return prev;
          }
        });
        message.success("Saved successfully!");
        return true;
      } else {
        message.error("User was not saved successfully.");
        return false;
      }
    } catch (error) {
      message.error("User was not saved successfully.");
      console.error("Error saving user:", error);
      return false;
    }
  };

  // --- Analytics computed variables ---
  const chartUser = tableData.find(u => u.UserID === chartUserId) || (tableData.length ? tableData[0] : null);
  const chartUserLogs = getUserLogsFor(chartUser, logData);
  const buckets = makeBuckets(chartUserLogs, 28);
  const counts = buckets.map(b => b.count);
  const SPARK_W = 420, SPARK_H = 90;
  const sparkLineD = sparkPathFromCounts(counts, SPARK_W, SPARK_H);
  const sparkAreaD = sparkLineD ? `${sparkLineD} L ${SPARK_W} ${SPARK_H} L 0 ${SPARK_H} Z` : '';
  const total28 = counts.reduce((a,b) => a + b, 0);
  const avg28 = Math.round((total28 / 28) * 10) / 10;
  // --- Sparkline geometry helpers for tooltip and hit-zones ---
  const maxC = Math.max(1, ...counts);
  const stepX = counts.length > 1 ? (SPARK_W / (counts.length - 1)) : SPARK_W;
  const points = counts.map((c, i) => ({ x: i * stepX, y: SPARK_H - (c / maxC) * SPARK_H }));
  const hoverPoint = sparkHoverIdx != null ? points[sparkHoverIdx] : null;
  const TIP_W = 120, TIP_H = 38;
  let tipX = 0, tipY = 0;
  if (hoverPoint) {
    tipX = Math.max(6, Math.min(SPARK_W - TIP_W - 6, hoverPoint.x - TIP_W / 2));
    tipY = hoverPoint.y - TIP_H - 10;
    if (tipY < 6) tipY = hoverPoint.y + 12;
  }

  // Export user activity logs to Excel
  const handleExportExcel = () => {
    // Use the full user activity logs (logData) for export, or filter as needed
    const userActivityData = logData;
    if (!Array.isArray(userActivityData) || userActivityData.length === 0) {
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(userActivityData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'User Activity Logs');
    XLSX.writeFile(workbook, 'user_activity_logs.xlsx');
  };
  const columns = [
    {
      title: "Username",
      dataIndex: "Username",
      key: "Username",
      ellipsis: true,
      width: 200,
      responsive: ["xs", "sm", "md"],
      render: (text, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Avatar size={20} style={{ backgroundColor: '#fde68a', color: '#111827' }}>
            {initialChar(record.FullName, text)}
          </Avatar>
          <span>{text}</span>
        </span>
      )
    },
    {
      title: "Last Login",
      dataIndex: "LastLoginDate",
      key: "LastLoginSwap",
      responsive: ["lg", "xl", "xxl"],
      width: 220,
      render: (val) => formatDateSA(val),
      sorter: (a, b) => {
        const ta = a?.LastLoginDate ? new Date(a.LastLoginDate).getTime() : 0;
        const tb = b?.LastLoginDate ? new Date(b.LastLoginDate).getTime() : 0;
        return ta - tb; // ascending comparator; defaultSortOrder handles initial descend
      },
      defaultSortOrder: 'descend',
      sortDirections: ['descend','ascend'],
    },
    { title: "Full Name", dataIndex: "FullName", key: "FullName", ellipsis: true, width: 200 },
    { title: "Created At", dataIndex: "CreatedAt", key: "CreatedAt", responsive: ["lg"], width: 180, render: (val) => formatDateSA(val) },
    {
      title: "Is Active",
      dataIndex: "IsActive",
      key: "IsActive",
      render: (val) => (val ? "Yes" : "No"),
      width: 100,
    },
    {
      title: "Role",
      dataIndex: "RoleId",
      key: "RoleId",
      render: (val) => roles.find((r) => r.id === val)?.name || val,
      width: 160,
    },
    {
      title: "Failed Login Attempts",
      dataIndex: "FailedLoginAttempts",
      key: "FailedLoginAttempts",
      responsive: ["xl"],
      width: 160,
    },
    { title: "Password Last Changed", dataIndex: "PasswordLastChanged", key: "PasswordLastChanged", responsive: ["xxl"], width: 200 },
    {
      title: "Password Change Required",
      dataIndex: "RequiresPasswordChange",
      key: "RequiresPasswordChange",
      render: (val) => (val ? "Yes" : "No"),
      responsive: ["xl"],
      width: 200,
    },
  ];

  return (
    <>
      <style>{`
        /* Theme-scoped luxe backdrop and surfaces */
        .admin-page.theme-dark{
          --appverse-bg:
            radial-gradient(1200px 300px at 10% -10%, rgba(253,224,71,0.10), transparent 60%),
            radial-gradient(1200px 300px at 90% -10%, rgba(234,179,8,0.10), transparent 60%),
            linear-gradient(180deg, #0b1220, #0b1220);
        }
        .admin-page.theme-light{
          --appverse-bg:
            radial-gradient(1200px 300px at 10% -10%, rgba(253,224,71,0.18), transparent 60%),
            radial-gradient(1200px 300px at 90% -10%, rgba(253,224,71,0.12), transparent 60%),
            linear-gradient(180deg, #f5f7fb, #ffffff);
        }
        .admin-page{ background: var(--appverse-bg) !important; }
        .ant-layout, .ant-layout-content, .appverse-content{ background: transparent !important; }
        /* Ensure the pushed content area still fills the viewport */
        .appverse-content{ min-height: 100vh; }
        /* —— Light theme overrides: preserve dimensions, swap surfaces/text —— */
        .admin-page.theme-light .analytics-card,
        .admin-page.theme-light .user-card,
        .admin-page.theme-light .log-card,
        .admin-page.theme-light .stacked,
        .admin-page.theme-light .toolbar-lux{
          color:#0b0b12;
          background:
            linear-gradient(180deg, rgba(255,255,255,0.92), rgba(248,250,252,0.86)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.12), transparent 60%);
          border:1px solid rgba(253,224,71,0.22);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.65), 0 10px 22px rgba(251,191,36,0.10);
        }
        .admin-page.theme-light .analytics-card a,
        .admin-page.theme-light .user-card a,
        .admin-page.theme-light .log-card a,
        .admin-page.theme-light .stacked a{ color:#0b0b12; }
        .admin-page.theme-light .user-card .meta,
        .admin-page.theme-light .log-meta,
        .admin-page.theme-light .log-time{ color:#334155; }

        /* Table surfaces/text */
        .admin-page.theme-light .lux-table .ant-table{
          background: linear-gradient(180deg, #ffffff, #f9fafb);
          border:1px solid rgba(253,224,71,0.22);
        }
        .admin-page.theme-light .lux-table .ant-table-thead > tr > th{
          background: linear-gradient(180deg, rgba(253,224,71,0.12), rgba(253,224,71,0.04));
          color:#0b0b12;
          border-bottom:1px solid rgba(253,224,71,0.22);
        }
        .admin-page.theme-light .lux-table .ant-table-tbody > tr > td{ color:#0b0b12; }
        .admin-page.theme-light .lux-table .ant-table-tbody > tr:nth-child(even) > td{ background: rgba(253,224,71,0.06); }
        .admin-page.theme-light .lux-table .ant-table-tbody > tr:hover > td{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); }

        /* Inputs in toolbar */
        .admin-page.theme-light .toolbar-lux .ant-input-affix-wrapper,
        .admin-page.theme-light .toolbar-lux .ant-input{
          background:#ffffff;
          color:#0b0b12;
          border:1px solid rgba(253,224,71,0.22);
        }
        .admin-page.theme-light .toolbar-lux .ant-input::placeholder{ color: rgba(2,6,23,0.45); }
        .admin-page.theme-light .toolbar-lux .ant-input-search-button{ color:#0b0b12; }

        /* Chips */
        .admin-page.theme-light .chip{ color:#334155; }
        .admin-page.theme-light .chip.filter.active{ color:#0b0b12; border-color: rgba(251,191,36,0.55); }
        /* Force luxe‑white text for ghost buttons in the popup (e.g., Close) */
        .popup-lux .btn-ghost,
        .popup-lux .popup-footer .btn-ghost,
        .popup-lux .ant-btn.btn-ghost{
          color:#f8fafc !important;
          text-shadow: 0 1px 2px rgba(0,0,0,0.6);
        }
        .popup-lux .btn-ghost:hover,
        .popup-lux .popup-footer .btn-ghost:hover,
        .popup-lux .ant-btn.btn-ghost:hover{
          color:#f8fafc !important;
        }
        /* Modal mask: dark, blurred overlay for focus */
        .ant-modal-mask {
          background-color: rgba(0, 0, 0, 0.75) !important;
          backdrop-filter: blur(6px);
        }
        /* Base foreground on dark surfaces → use luxe white for readability */
        .analytics-card, .user-card, .log-card, .stacked, .toolbar-lux{ color:#f8fafc; }
        .analytics-card a, .user-card a, .log-card a, .stacked a{ color:#f8fafc; }
        /* Keep existing nuanced metas/badges as-is via their specific rules */
        /* — Responsive card grids — */
        .user-cards-grid{ display:grid; grid-template-columns: 1fr; gap:12px; }
        @media (min-width: 768px){
          .user-cards-grid{ grid-template-columns: repeat(2, minmax(280px, 1fr)); }
          .logs-list{ grid-template-columns: repeat(2, minmax(300px, 1fr)); }
        }
        @media (min-width: 1200px){
          .user-cards-grid{ grid-template-columns: repeat(3, minmax(300px, 1fr)); }
          .logs-list{ grid-template-columns: repeat(3, minmax(320px, 1fr)); }
        }


        /* — Luxe analytics card — */
        .analytics-card{
          background:
            linear-gradient(180deg, rgba(2,6,23,0.78), rgba(2,6,23,0.64)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.08), transparent 60%);
          border:1px solid rgba(253,224,71,0.18);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 22px rgba(251,191,36,0.10);
          border-radius:12px;
        }
        .analytics-head{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
        .analytics-head .user-picker{ display:flex; align-items:center; gap:10px; }
        .analytics-head .chips{ display:flex; gap:8px; flex-wrap:wrap; }
        .analytics-card .chip{ padding:6px 10px; border-radius:999px; font-size:12px; color:#e5e7eb; border:1px solid rgba(253,224,71,0.28); background: linear-gradient(180deg, rgba(253,224,71,0.08), rgba(253,224,71,0.03)); }
        .chip.filter{ cursor:pointer; user-select:none; }
        .chip.filter.active{ background: linear-gradient(180deg, rgba(253,224,71,0.24), rgba(253,224,71,0.08)); border-color: rgba(251,191,36,0.55); color:#fefce8; }
        .spark-wrap{ margin-top:10px; position: relative; }
        .sparkline{ width:100%; height:120px; display:block; }
        .spark-xlabels{ display:flex; justify-content:space-between; color:#94a3b8; font-size:11px; margin-top:4px; }
        /* — Luxe user cards for very small screens — */
        .user-card{ 
          background:
            linear-gradient(180deg, rgba(2,6,23,0.82), rgba(2,6,23,0.72)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.08), transparent 60%);
          border:1px solid rgba(253,224,71,0.22);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 22px rgba(251,191,36,0.10);
          border-radius:12px;
          transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
        }
        .user-card:hover{ transform: translateY(-2px); filter:saturate(1.04); box-shadow: 0 12px 26px rgba(251,191,36,0.18); }
        .user-card .title{ font-weight:700; letter-spacing:.2px; background-image: linear-gradient(92deg,#f8fafc 0%, #fde68a 22%, #fbbf24 44%, #fde68a 66%, #f8fafc 100%); -webkit-background-clip:text; background-clip:text; color: transparent; background-size:200% 100%; transition: background-position 1.1s ease; }
        .user-card:hover .title{ background-position:100% 0; }
        .user-card .meta{ font-size:12px; color:#94a3b8; }
        .user-card .divider{ height:1px; margin:10px 0; background: linear-gradient(90deg, rgba(253,224,71,0), rgba(253,224,71,0.28), rgba(253,224,71,0)); }
        .user-card .btn-primary{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); border:1px solid rgba(251,191,36,0.55); color:#0b0b12; box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18); }
        .user-card .btn-primary:hover{ filter:saturate(1.06) brightness(1.04); box-shadow: 0 12px 26px rgba(251,191,36,0.24); }
        .user-card .btn-ghost{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); border:1px solid rgba(251,191,36,0.28); color:#e5e7eb; }
        .user-card .btn-ghost:hover{ filter:saturate(1.04); box-shadow: 0 8px 18px rgba(251,191,36,0.16); }
        .user-card .status-active{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)) !important; border:1px solid rgba(251,191,36,0.55) !important; color:#0b0b12 !important; }
        .user-card .status-inactive{ background: linear-gradient(180deg, rgba(148,163,184,0.35), rgba(30,41,59,0.35)) !important; border:1px solid rgba(148,163,184,0.45) !important; color:#e5e7eb !important; }
        /* Drag affordances for user cards (desktop) */
        .user-card-wrap{ position: relative; }
        .user-card-wrap .card-handle{
          position:absolute; top:8px; right:8px; width:28px; height:28px; border-radius:8px;
          display:inline-flex; align-items:center; justify-content:center; cursor:grab;
          background: linear-gradient(180deg, rgba(253,224,71,0.14), rgba(253,224,71,0.06));
          border:1px solid rgba(251,191,36,0.35); color:#fefce8; z-index:2;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 6px 14px rgba(251,191,36,0.12);
        }
        .user-card-wrap .card-handle:active{ cursor:grabbing; }
        .user-card-wrap.dragging{ opacity:.75; transform: scale(.99); }
        .user-card-wrap.drag-over::before{
          content:""; position:absolute; left:8px; right:8px; top:-6px; height:4px; border-radius:2px;
          background: linear-gradient(90deg, rgba(253,224,71,0), rgba(253,224,71,0.9), rgba(253,224,71,0));
          box-shadow: 0 6px 14px rgba(251,191,36,0.25);
        }
        @media (max-width: 767px){ .user-card-wrap .card-handle{ display:none; } .user-card-wrap.drag-over::before{ display:none; } }
        /* — Luxe big-screen user table — */
        .lux-table .ant-table{ 
          background: linear-gradient(180deg, rgba(2,6,23,0.68), rgba(2,6,23,0.54));
          border:1px solid rgba(253,224,71,0.18);
          border-radius:12px;
          overflow:hidden;
        }
        .lux-table .ant-table-thead > tr > th{
          background: linear-gradient(180deg, rgba(253,224,71,0.20), rgba(253,224,71,0.06));
          color:#f8fafc;
          border-bottom:1px solid rgba(253,224,71,0.24);
          padding:10px 12px;
        }
        .lux-table .ant-table-tbody > tr > td{ padding:8px 10px; color:#f8fafc; }
        .lux-table .ant-table-tbody > tr:nth-child(even) > td{ background: rgba(253,224,71,0.03); }
        .lux-table .ant-table-tbody > tr:hover > td{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); }
        /* Force cell content to luxe white */
        .lux-table .ant-table-tbody > tr > td a,
        .lux-table .ant-table-tbody > tr > td .ant-typography,
        .lux-table .ant-table-tbody > tr > td .ant-tag,
        .lux-table .ant-table-tbody > tr > td span,
        .lux-table .ant-table-tbody > tr > td div{ color:#f8fafc; }
        .lux-table .ant-table-thead .ant-table-column-sorter,
        .lux-table .ant-table-thead .ant-table-column-sorter-inner{ color:#f8fafc; }
        .lux-table .ant-pagination{ margin:8px 12px; }

        /* Header separation when using internal scroll */
        .admin-page .lux-table .ant-table-header{ box-shadow: 0 1px 0 rgba(251,191,36,0.25); }
        /* — Luxe toolbar — */
        .toolbar-lux{ 
          margin-top: 0; 
          padding: 8px; 
          border-radius: 12px; 
          background:
            linear-gradient(180deg, rgba(2,6,23,0.72), rgba(2,6,23,0.62)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.08), transparent 60%);
          border:1px solid rgba(253,224,71,0.18);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 18px rgba(251,191,36,0.10);
        }
        .toolbar-lux .btn-gold{ 
          background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); 
          border:1px solid rgba(251,191,36,0.55); 
          color:#0b0b12; 
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18); 
        }
        .toolbar-lux .btn-gold:hover{ filter:saturate(1.06) brightness(1.05); box-shadow: 0 12px 26px rgba(251,191,36,0.24); }
        .toolbar-lux .btn-ghost-gold{ 
          background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); 
          border:1px solid rgba(251,191,36,0.28); 
          color:#e5e7eb; 
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .toolbar-lux .btn-ghost-gold:hover{ filter:saturate(1.04); box-shadow: 0 8px 18px rgba(251,191,36,0.16); }
        /* — Luxe buttons (global on admin page) — */
        .btn-lux{ 
          border-radius: 999px; 
          position: relative; 
          overflow: hidden; 
          transition: transform .12s ease, filter .2s ease, box-shadow .2s ease; 
        }
        .btn-lux::after{
          content: ""; position:absolute; inset:0; transform: translateX(-120%);
          background: linear-gradient(120deg, rgba(255,255,255,0.00) 0%, rgba(255,255,255,0.45) 35%, rgba(255,255,255,0.00) 70%);
          transition: transform .75s ease; pointer-events:none;
        }
        .btn-lux:hover::after{ transform: translateX(120%); }
        .btn-lux:active{ transform: translateY(1px) scale(.99); }
        .btn-lux:focus-visible{ outline:none; box-shadow: 0 0 0 3px rgba(251,191,36,0.28); }
        .btn-lux[disabled], .btn-lux:disabled{ opacity:.6; filter: grayscale(.1); box-shadow:none; }
        /* Icon-only pill nuance */
        .btn-icon-pill.btn-lux{ padding:0; width:40px; height:40px; }
        .btn-icon-pill.btn-lux:active{ transform: translateY(1px) scale(.98); }
        .toolbar-lux .btn-icon-pill{ width:40px; height:40px; padding:0; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; }
        .toolbar-lux .ant-input-search{ border-radius: 10px; overflow: hidden; }
        .toolbar-lux .ant-input-affix-wrapper, 
        .toolbar-lux .ant-input{ 
          background: linear-gradient(180deg, rgba(2,6,23,0.72), rgba(2,6,23,0.62)); 
          color: #e5e7eb; 
          border:1px solid rgba(253,224,71,0.18); 
        }
        .toolbar-lux .ant-input::placeholder{ color: rgba(248,250,252,0.55); }
        .toolbar-lux .ant-input-affix-wrapper-focused{ box-shadow: 0 0 0 3px rgba(253,224,71,0.18); border-color: rgba(253,224,71,0.45) !important; }
        .toolbar-lux .ant-input-search-button{ 
          background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); 
          border:1px solid rgba(251,191,36,0.55); 
          color:#0b0b12; 
        }
        .toolbar-lux .ant-input-search-button:hover{ filter:saturate(1.06) brightness(1.05); }
        /* Segmented (Cards/Table) */
        .toolbar-lux .ant-segmented{ background: linear-gradient(180deg, rgba(253,224,71,0.12), rgba(253,224,71,0.04)); border:1px solid rgba(251,191,36,0.35); border-radius: 999px; }
        .toolbar-lux .ant-segmented-item{ color:#e5e7eb; }
        .toolbar-lux .ant-segmented-thumb{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); box-shadow: 0 6px 16px rgba(251,191,36,0.25); }
        .toolbar-lux .ant-segmented-item-selected{ color:#0b0b12; font-weight:600; }
        /* Admin page: tighten top spacing under the header */
        .admin-page{ margin-top: -12px; }
        @media (max-width: 767px){ .admin-page{ margin-top: -8px; } }

        /* Admin desktop: widen content and tighten vertical rhythm */
        @media (min-width: 992px){
          .admin-page{ margin-top: -16px; }
          .admin-page .tabled{ max-width: none !important; width: 100%; padding-right: 16px; }
        }
        .admin-page .ant-card-head{ padding: 10px 12px; min-height: 36px; }
        .admin-page .ant-card-body{ padding: 12px; }
        .admin-page .criclebox.tablespace.mb-24{ margin-bottom: 16px !important; }
        .admin-page .criclebox.tablespace.mt-24{ margin-top: 16px !important; }
        .admin-page .analytics-card .sparkline{ height: 96px; }
        /* — Seamless stacked flow across Overview → User Data → Activity Logs — */
        .admin-page .stacked{
          position: relative;
          background:
            linear-gradient(180deg, rgba(2,6,23,0.78), rgba(2,6,23,0.64)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.08), transparent 60%);
          border:1px solid rgba(253,224,71,0.18);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 10px 22px rgba(251,191,36,0.10);
          border-radius:16px;
          overflow: hidden;
        }
        /* tighten spacing between stacked cards */
        .admin-page .stacked + .stacked{ margin-top: -14px; }
        /* bottom gold hairline on each card */
        .admin-page .stacked::after{
          content:""; position:absolute; left:12px; right:12px; bottom:-1px; height:1px;
          background: linear-gradient(90deg, rgba(253,224,71,0), rgba(253,224,71,0.35), rgba(253,224,71,0));
          pointer-events:none; opacity:.7;
        }
        /* true-overlap: remove seam and layer correctly */
        .admin-page .stacked::after{ display: none; }
        .admin-page .stacked{ z-index: 1; }
        .admin-page .stacked:hover{ z-index: 2; }
        .admin-page .stacked + .stacked{ border-top-color: transparent; }
        @media (max-width: 767px){ .admin-page .stacked + .stacked{ margin-top: 6px; } }
        /* soft connector glow at the top of every card except the first */
        .admin-page .stacked + .stacked::before{
          content:""; position:absolute; left:10px; right:10px; top:-16px; height:22px;
          background: radial-gradient(60% 80% at 50% 0%, rgba(251,191,36,0.30), rgba(251,191,36,0));
          filter: blur(6px); pointer-events:none; opacity:.6;
        }
        /* unify card header look and remove heavy borders */
        .admin-page .stacked .ant-card-head{ background: transparent; border-bottom:1px solid rgba(253,224,71,0.22); padding: 10px 14px; }
        .admin-page .stacked .ant-card-head-title{ color:#f8fafc; font-weight:700; letter-spacing:.2px; }
        .admin-page .stacked .ant-card-body{ padding: 14px; }
        /* — Luxe mobile log cards — */
        .logs-list{ display:grid; grid-template-columns:1fr; gap:10px; }
        .log-card{
          background:
            linear-gradient(180deg, rgba(2,6,23,0.82), rgba(2,6,23,0.72)),
            radial-gradient(120% 160% at -20% -20%, rgba(253,224,71,0.08), transparent 60%);
          border:1px solid rgba(253,224,71,0.22);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 18px rgba(251,191,36,0.10);
          border-radius:12px;
          transition: transform .2s ease, box-shadow .2s ease, filter .2s ease;
        }
        .log-card:hover{ transform: translateY(-2px); filter:saturate(1.04); box-shadow: 0 12px 26px rgba(251,191,36,0.18); }
        .log-card .log-title{ font-weight:700; letter-spacing:.2px; background-image: linear-gradient(92deg,#f8fafc 0%, #fde68a 22%, #fbbf24 44%, #fde68a 66%, #f8fafc 100%); -webkit-background-clip:text; background-clip:text; color: transparent; background-size:200% 100%; transition: background-position 1.1s ease; }
        .log-card:hover .log-title{ background-position:100% 0; }
        .log-card .divider{ height:1px; margin:10px 0; background: linear-gradient(90deg, rgba(253,224,71,0), rgba(253,224,71,0.28), rgba(253,224,71,0)); }
        .log-card .btn-primary{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); border:1px solid rgba(251,191,36,0.55); color:#0b0b12; box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18); }
        .log-card .btn-primary:hover{ filter:saturate(1.06) brightness(1.04); box-shadow: 0 12px 26px rgba(251,191,36,0.24); }
        .log-card .btn-ghost{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); border:1px solid rgba(251,191,36,0.28); color:#e5e7eb; }
        .log-card .btn-ghost:hover{ filter:saturate(1.04); box-shadow: 0 8px 18px rgba(251,191,36,0.16); }
        .log-card .head{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:6px; }
        .log-badge{ padding:4px 8px; border-radius:999px; font-size:11px; border:1px solid rgba(253,224,71,0.28); background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); color:#e5e7eb; }
        .log-time{ display:flex; align-items:center; gap:6px; font-size:11px; color:#cbd5e1; }
        .log-title{ font-weight:700; color:#f8fafc; }
        .log-meta{ font-size:12px; color:#94a3b8; }
        .log-footer{ display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:8px; }
        .log-user{ display:flex; align-items:center; gap:6px; color:#e5e7eb; font-size:12px; }
        /* tones */
        .log-badge.ok{ background: linear-gradient(180deg, rgba(34,197,94,0.18), rgba(34,197,94,0.06)); border-color: rgba(34,197,94,0.45); color:#bbf7d0; }
        .log-badge.warn{ background: linear-gradient(180deg, rgba(251,191,36,0.18), rgba(251,191,36,0.06)); border-color: rgba(251,191,36,0.45); color:#fde68a; }
        .log-badge.err{ background: linear-gradient(180deg, rgba(239,68,68,0.18), rgba(239,68,68,0.06)); border-color: rgba(239,68,68,0.45); color:#fecaca; }
        .log-badge.info{ background: linear-gradient(180deg, rgba(59,130,246,0.18), rgba(59,130,246,0.06)); border-color: rgba(59,130,246,0.45); color:#bfdbfe; }
        @media (max-width: 767px){
          .logs-list{ gap:8px; }
          .log-card .head{ margin-bottom:4px; }
          .log-card .log-title{ font-size:13px; }
          .log-meta{ font-size:11px; }
          .log-badge{ padding:3px 6px; font-size:10px; }
          .log-card .divider{ margin:8px 0; }
          .log-time{ font-size:10px; }
          .log-user{ font-size:11px; }
          .log-footer{ margin-top:6px; flex-wrap:wrap; gap:6px; }
        }
      `}</style>
      <div
        className={`appverse-content table-container ${theme === "dark" ? "luxe-dark" : "luxe-light"}`}
        style={{
          background: theme === "dark" ? "#000" : "#fff",
          minHeight: "100vh",
          marginLeft: "var(--sidenav-width, 88px)",
          padding: "0 20px 0 12px",
          position: "relative",
          zIndex: 1002,
        }}
      >
        <div className={`admin-page theme-${theme}`}>
          <div className="tabled">
          <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-16 luxe-dark-bg"
              title="User Overview"
            >
              <div className="analytics-head">
                <div className="user-picker">
                  <Avatar size={28} style={{ backgroundColor: '#fde68a', color: '#111827' }}>
                    {initialChar(chartUser?.FullName, chartUser?.Username)}
                  </Avatar>
                  <Select
                    value={chartUserId}
                    onChange={setChartUserId}
                    showSearch
                    optionFilterProp="children"
                    placeholder="Select a user"
                    style={{ minWidth: 240 }}
                  >
                    {tableData.map((u) => (
                      <Option key={u.UserID} value={u.UserID}>
                        {u.Username} — {u.FullName}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div className="chips">
                  <span className="chip"><strong>{total28}</strong>&nbsp;actions (28d)</span>
                  <span className="chip"><strong>{avg28}</strong>&nbsp;avg/day</span>
                  <span className="chip"><strong>{formatDateSA(chartUser?.LastLoginDate)}</strong>&nbsp;last login</span>
                </div>
                <div style={{ marginLeft: 'auto', display:'flex', alignItems:'center', gap:8 }} className="mode-toggle">
                  <span className="meta" style={{ opacity: 0.9 }}>{theme === 'dark' ? 'Dark' : 'Light'} mode</span>
                  <Segmented
                    options={[{ label: 'Dark', value: 'dark' }, { label: 'Light', value: 'light' }]}
                    value={theme}
                    onChange={setTheme}
                  />
                </div>
              </div>
              <div className="spark-wrap">
                <svg className="sparkline" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="goldStroke" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#f8fafc" />
                      <stop offset="22%" stopColor="#fde68a" />
                      <stop offset="44%" stopColor="#fbbf24" />
                      <stop offset="66%" stopColor="#fde68a" />
                      <stop offset="100%" stopColor="#f8fafc" />
                    </linearGradient>
                    <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(251,191,36,0.35)" />
                      <stop offset="100%" stopColor="rgba(251,191,36,0.00)" />
                    </linearGradient>
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="rgba(251,191,36,0.35)" />
                    </filter>
                  </defs>

                  {/* Week separators (every Monday) */}
                  <g style={{ pointerEvents: 'none' }}>
                    {buckets.map((b, i) => (
                      b.dow === 1 ? (
                        <line key={`wk-${b.key}`} x1={points[i].x} y1={0} x2={points[i].x} y2={SPARK_H} stroke="rgba(253,224,71,0.12)" strokeDasharray="2 6" />
                      ) : null
                    ))}
                  </g>

                  {/* Area/line (pointer-events disabled so hit-zones receive hover) */}
                  <g style={{ pointerEvents: 'none' }}>
                    <path d={sparkAreaD} fill="url(#goldFill)" />
                    <path d={sparkLineD} stroke="url(#goldStroke)" strokeWidth="2.5" fill="none" filter="url(#glow)" />
                  </g>

                  {/* Hover guideline, point, and tooltip */}
                  {hoverPoint && (
                    <g style={{ pointerEvents: 'none' }}>
                      <line x1={hoverPoint.x} y1={0} x2={hoverPoint.x} y2={SPARK_H} stroke="rgba(253,224,71,0.28)" strokeDasharray="4 4" />
                      <circle cx={hoverPoint.x} cy={hoverPoint.y} r={3.5} fill="#fde68a" stroke="#fbbf24" filter="url(#glow)" />
                      <rect x={tipX} y={tipY} width={TIP_W} height={TIP_H} rx={8} fill="rgba(11,19,32,0.96)" stroke="rgba(251,191,36,0.45)" />
                      <text x={tipX + 10} y={tipY + 16} fill="#f8fafc" fontSize="11">{buckets[sparkHoverIdx].label}</text>
                      <text x={tipX + 10} y={tipY + 30} fill="#fde68a" fontSize="12" fontWeight="bold">{counts[sparkHoverIdx]} actions</text>
                    </g>
                  )}

                  {/* Transparent hit-zones per bucket */}
                  <g>
                    {points.map((p, i) => {
                      const x0 = i === 0 ? 0 : (points[i - 1].x + p.x) / 2;
                      const x1 = i === points.length - 1 ? SPARK_W : (p.x + points[i + 1].x) / 2;
                      return (
                        <rect
                          key={i}
                          x={x0}
                          y={0}
                          width={Math.max(1, x1 - x0)}
                          height={SPARK_H}
                          fill="transparent"
                          onMouseEnter={() => setSparkHoverIdx(i)}
                          onMouseLeave={() => setSparkHoverIdx(null)}
                        />
                      );
                    })}
                  </g>
                </svg>
                <div className="spark-xlabels">
                  {buckets.map((b, i) => (
                    <span key={b.key}>{b.dow === 1 ? b.label : ''}</span>
                  ))}
                </div>
              </div>
            </Card>
            <Card
              bordered={false}
              className="criclebox tablespace mb-16 luxe-dark-bg"
              title="User Data"
            >
              <div
                className="toolbar-lux"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                  justifyContent: isMobile ? "space-between" : "flex-start",
                }}
              >
                <Button
                  type="default"
                  className="luxe-btn"
                  onClick={handleAddClick}
                >
                  New User
                </Button>

                {isMobile ? (
                  <Tooltip title="Export to Excel">
                    <Button
                      type="default"
                      className="luxe-btn"
                      onClick={handleExportExcel}
                      aria-label="Export to Excel"
                      icon={<FileExcelOutlined />}
                    />
                  </Tooltip>
                ) : (
                  <Button
                    type="default"
                    className="luxe-btn"
                    onClick={handleExportExcel}
                    style={{ marginRight: 8 }}
                    icon={<FileExcelOutlined />}
                  >
                    Export to Excel
                  </Button>
                )}

                <Search
                  placeholder="Search by Username or Full Name"
                  className="luxe-search"
                  enterButton={<Button className="luxe-search-btn" icon={<SearchOutlined />} />}
                  onSearch={handleSearch}
                  style={{ width: isMobile ? "100%" : 300, marginLeft: isMobile ? 0 : 16 }}
                  allowClear
                />
                <div style={{ marginLeft: 'auto', display: isMobile ? 'none' : 'inline-flex' }} className="mode-toggle">
                  <Segmented
                    options={[{label:'Cards', value:'cards'}, {label:'Table', value:'table'}]}
                    value={viewUsers}
                    onChange={setViewUsers}
                  />
                </div>
              </div>
              <div className="table-responsive" style={{ marginTop: 16 }}>
                {!isMobile && viewUsers === 'table' ? (
                  <Table
                    className="luxe-table"
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    pagination={{ pageSize, showSizeChanger: !isMobile, size: 'small' }}
                    rowKey="UserID"
                    size="small"
                    tableLayout="fixed"
                    scroll={scrollY ? { y: scrollY } : undefined}
                    onRow={(record) => ({ onClick: () => handleRowClick(record) })}
                  />
                ) : (
                  <div role="list" className="user-cards-grid">
                    {orderedUsers.map((u) => {
                      const k = keyForUser(u);
                      return (
                        <div
                          key={k}
                          className={`user-card-wrap ${dragUserId===k ? 'dragging' : ''} ${overUserId===k ? 'drag-over' : ''}`}
                          draggable={!isMobile}
                          onDragStart={onCardDragStart(k)}
                          onDragOver={onCardDragOver(k)}
                          onDrop={onCardDrop(k)}
                          onDragEnd={onCardDragEnd}
                        >
                          {!isMobile && (
                            <span className="card-handle" title="Drag to reorder" draggable={!isMobile} onDragStart={onCardDragStart(k)}>
                              <HolderOutlined />
                            </span>
                          )}
                          <Card
                            size="small"
                            className="user-card luxe-dark-bg"
                            style={{ borderRadius: 12, overflow: "hidden" }}
                            bodyStyle={{ padding: 12 }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Avatar size={20} style={{ backgroundColor: '#fde68a', color: '#111827' }}>
                                    {initialChar(u.FullName, u.Username)}
                                  </Avatar>
                                  <div className="title">{u.Username || "-"}</div>
                                </div>
                                <div className="meta" style={{ opacity: 0.9 }}>{u.FullName || "-"}</div>
                                <div className="meta" style={{ marginTop: 4 }}>Created: {formatDateSA(u.CreatedAt)}</div>
                                <div className="meta">Last seen: {formatDateSA(u.LastLoginDate)}</div>
                              </div>
                              <div style={{ textAlign: "right", display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                                {u.CreatedAt ? (
                                  <span
                                    className="log-time"
                                    title={`Created: ${formatDateSA(u.CreatedAt)}`}
                                  >
                                    <ClockCircleOutlined />joined {timeAgoSA(u.CreatedAt)}
                                  </span>
                                ) : null}
                                <span
                                  className="log-time"
                                  title={u.LastLoginDate ? `Last seen: ${formatDateSA(u.LastLoginDate)}` : 'Last seen: -'}
                                >
                                  <ClockCircleOutlined />last seen {timeAgoSA(u.LastLoginDate)}
                                </span>
                                <Tag className={u.IsActive ? "status-active" : "status-inactive"}>{u.IsActive ? "Active" : "Inactive"}</Tag>
                                <div className="meta">Role: {roles.find((r) => r.id === u.RoleId)?.name || u.RoleId || "-"}</div>
                              </div>
                            </div>
                            <div className="divider" />
                            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                              <Button
                                type="default"
                                className="luxe-btn"
                                onClick={() => {
                                  const userLogs = logData.filter((log) => log.UserID === u.UserID);
                                  setFocusActivity(false);
                                  setSelectedPerson(u);
                                  setSelectedUserLogs(userLogs);
                                  setIsModalVisible(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                type="default"
                                className="luxe-btn"
                                onClick={() => {
                                  const userLogs = logData.filter((log) => log.UserID === u.UserID);
                                  setFocusActivity(true);
                                  setSelectedPerson(u);
                                  setSelectedUserLogs(userLogs);
                                  setIsModalVisible(true);
                                }}
                              >
                                Logs
                              </Button>
                            </div>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>
            <Card
              bordered={false}
              className="criclebox tablespace luxe-dark-bg"
              title="User Activity Logs"
            >
              <div
                className="toolbar-lux"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  alignItems: "center",
                  justifyContent: isMobile ? "space-between" : "flex-start",
                  marginBottom: 12,
                }}
              >
                <Search
                  placeholder="Search logs by username, type, action or by"
                  className="luxe-search"
                  enterButton={<Button className="luxe-search-btn" icon={<SearchOutlined />} />}
                  onSearch={setLogSearch}
                  style={{ width: isMobile ? "100%" : 300, marginBottom: 0 }}
                  allowClear
                />
                <div style={{ marginLeft: 'auto', display: isMobile ? 'none' : 'inline-flex' }} className="mode-toggle">
                  <Segmented
                    options={[{label:'Cards', value:'cards'}, {label:'Table', value:'table'}]}
                    value={viewLogs}
                    onChange={setViewLogs}
                  />
                </div>
              </div>
              {!isMobile ? (
                viewLogs === 'table' ? (
                  <Table
                    className="luxe-table"
                    columns={[
                      { 
                        title: "Time", dataIndex: "ActionTime", key: "ActionTime", width: 200, 
                        render: (v) => formatDateSA(v),
                        sorter: (a, b) => {
                          const ta = a?.ActionTime ? new Date(a.ActionTime).getTime() : 0;
                          const tb = b?.ActionTime ? new Date(b.ActionTime).getTime() : 0;
                          return ta - tb; // ascending comparator; defaultSortOrder sets initial descend
                        },
                        defaultSortOrder: 'descend',
                        sortDirections: ['descend','ascend'],
                      },
                      { title: "Username", dataIndex: "Username", key: "Username", ellipsis: true },
                      { title: "Type", dataIndex: "ResultType", key: "ResultType", responsive: ["md"] },
                      { title: "Action", dataIndex: "Action", key: "Action", ellipsis: true },
                      { title: "By", dataIndex: "PerformedBy", key: "PerformedBy", responsive: ["lg"] },
                      { title: "Details", dataIndex: "Details", key: "Details", responsive: ["xl"], ellipsis: true },
                    ]}
                    dataSource={sortedLogDataDesc}
                    pagination={{ pageSize: 10, size: 'default' }}
                    rowKey="LogRef"
                    size="middle"
                    scroll={scrollY ? { x: 960, y: scrollY } : { x: 960 }}
                  />
                ) : (
                  <div className="logs-list" role="list">
                    {mobileGroups.map((g) => {
                      const last = g.last ? timeAgoSA(g.last) : '-';
                      const typeTop = topEntries(g.types, 3);
                      const actTop = topEntries(g.actions, 3);
                      const detTop = topEntries(g.details, 2);
                      return (
                        <Card key={`grp-d-${g.username}`}
                              size="small" className="log-card group-card luxe-dark-bg" bodyStyle={{ padding: 12 }}>
                          <div className="head">
                            <span className="log-user"><Avatar size={18} style={{ backgroundColor: '#fde68a', color: '#111827' }}>{initialChar(g.username)}</Avatar>{g.username}</span>
                            <span className="log-time"><ClockCircleOutlined />{last}</span>
                          </div>
                          <div className="log-title">{g.total} actions in view</div>
                          {typeTop.length ? (
                            <div className="chips" style={{ marginTop: 6 }}>
                              {typeTop.map(([t,c]) => (
                                <span key={t} className={`log-badge ${toneForType(t)}`}>{t}: {c}</span>
                              ))}
                            </div>
                          ) : null}
                          {actTop.length ? (
                            <div className="log-meta" style={{ marginTop: 8 }}>
                              {actTop.map(([a,c]) => (
                                <div key={a} style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{a}</span>
                                  <strong>x{c}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {detTop.length ? (
                            <div className="log-meta" style={{ marginTop: 8 }}>
                              {detTop.map(([d,c]) => (
                                <div key={d} style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{String(d).slice(0,80)}{String(d).length>80?'…':''}</span>
                                  <strong>x{c}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <div className="divider" />
                          <div className="log-footer">
                            {g.performers && g.performers.length ? (
                              <span className="log-user"><UserOutlined />{g.performers.length} actor{g.performers.length>1?'s':''}</span>
                            ) : <span />}
                            <div style={{ display:'flex', gap:6 }}>
                              <Button type="default" className="luxe-btn" onClick={() => {
                                const u = tableData.find(x => x.Username === g.username);
                                const userLogs = logData.filter((l) => (l.Username || '') === g.username);
                                if(u){ setFocusActivity(true); setSelectedPerson(u); setSelectedUserLogs(userLogs); setIsModalVisible(true); }
                              }}>Open logs</Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  <div className="chips" style={{ marginBottom: 8 }}>
                    <span className="chip"><strong>{filteredLogData.length}</strong>&nbsp;logs</span>
                    <span className="chip"><strong>{new Set(filteredLogData.map(l => l.Username)).size}</strong>&nbsp;users</span>
                    <span className="chip"><strong>{filteredLogData.filter(l => {
                      const d = new Date(l.ActionTime); d.setHours(d.getHours()+2);
                      const today = new Date(); today.setHours(0,0,0,0);
                      return d >= today;
                    }).length}</strong>&nbsp;today</span>
                  </div>

                  <div className="chips" style={{ marginBottom: 8 }}>
                    <span className={`chip-filter ${logFilter==='all' ? 'active' : ''}`} onClick={() => setLogFilter('all')}>All</span>
                    <span className={`chip-filter ${logFilter==='success' ? 'active' : ''}`} onClick={() => setLogFilter('success')}>Success</span>
                    <span className={`chip-filter ${logFilter==='errors' ? 'active' : ''}`} onClick={() => setLogFilter('errors')}>Errors</span>
                    <span className={`chip-filter ${logFilter==='today' ? 'active' : ''}`} onClick={() => setLogFilter('today')}>Today</span>
                  </div>

                  <div className="logs-list" role="list">
                    {mobileGroupsToShow.map((g) => {
                      const last = g.last ? timeAgoSA(g.last) : '-';
                      const typeTop = topEntries(g.types, 2);
                      const actTop = topEntries(g.actions, 2);
                      const detTop = topEntries(g.details, 1);
                      return (
                        <Card key={`grp-${g.username}`}
                              size="small" className="log-card group-card luxe-dark-bg" bodyStyle={{ padding: 10 }}>
                          <div className="head">
                            <span className="log-user"><Avatar size={18} style={{ backgroundColor: '#fde68a', color: '#111827' }}>{initialChar(g.username)}</Avatar>{g.username}</span>
                            <span className="log-time"><ClockCircleOutlined />{last}</span>
                          </div>
                          <div className="log-title">{g.total} actions in view</div>
                          {/* Types summary */}
                          {typeTop.length ? (
                            <div className="chips" style={{ marginTop: 6 }}>
                              {typeTop.map(([t,c]) => (
                                <span key={t} className={`log-badge ${toneForType(t)}`}>{t}: {c}</span>
                              ))}
                            </div>
                          ) : null}
                          {/* Top actions */}
                          {actTop.length ? (
                            <div className="log-meta" style={{ marginTop: 8 }}>
                              {actTop.map(([a,c]) => (
                                <div key={a} style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{a}</span>
                                  <strong>x{c}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          {/* Top details */}
                          {detTop.length ? (
                            <div className="log-meta" style={{ marginTop: 8 }}>
                              {detTop.map(([d,c]) => (
                                <div key={d} style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                                  <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'70%' }}>{String(d).slice(0,80)}{String(d).length>80?'…':''}</span>
                                  <strong>x{c}</strong>
                                </div>
                              ))}
                            </div>
                          ) : null}
                          <div className="divider" />
                          <div className="log-footer">
                            {g.performers && g.performers.length ? (
                              <span className="log-user"><UserOutlined />{g.performers.length} actor{g.performers.length>1?'s':''}</span>
                            ) : <span />}
                            <div style={{ display:'flex', gap:6 }}>
                              <Button type="default" className="luxe-btn" onClick={() => {
                                const u = tableData.find(x => x.Username === g.username);
                                const userLogs = logData.filter((l) => (l.Username || '') === g.username);
                                if(u){ setFocusActivity(true); setSelectedPerson(u); setSelectedUserLogs(userLogs); setIsModalVisible(true); }
                              }}>Open logs</Button>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  {mobileLogLimit < mobileGroups.length && (
                    <div style={{ display:'flex', justifyContent:'center', marginTop: 10 }}>
                      <Button type="default" className="luxe-btn" onClick={() => setMobileLogLimit(mobileLogLimit + 12)}>Load more</Button>
                    </div>
                  )}
                </>
              )}
            </Card>
          </Col>
          </Row>
          </div>
        </div>
      </div>

      {/* Popup component */}
      {selectedPerson && (
        <Popup
          visible={isModalVisible}
          onClose={handleCloseModal}
          personData={selectedPerson}
          onSave={handleSavePerson}
          selectedUserLogs={selectedUserLogs}
          focusActivityOnOpen={focusActivity}
        />
      )}
    </>
  );
}

// Editable Popup Modal with Ant Design Form
export function Popup({ visible, onClose, personData, onSave, selectedUserLogs, focusActivityOnOpen }) {
  const [form] = Form.useForm();
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const scrollContainerRef = useRef(null);
  const activityRef = useRef(null);
  const filteredLogs = selectedUserLogs.filter((log) =>
    log.Action?.toLowerCase().includes(logSearchTerm.toLowerCase())
  );
  const filteredLogsDesc = React.useMemo(() => {
    const ts = (x) => (x?.ActionTime ? new Date(x.ActionTime).getTime() : 0);
    return [...filteredLogs].sort((a, b) => ts(b) - ts(a));
  }, [filteredLogs]);

  useEffect(() => {
    form.setFieldsValue(personData);
  }, [personData, form]);

  useEffect(() => {
    if (visible && focusActivityOnOpen) {
      const el = activityRef.current;
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 50);
      }
    }
  }, [visible, focusActivityOnOpen]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const result = await onSave(values);
      if (result) {
        onClose();
      }
      // If not successful, keep the modal open
    } catch (err) {
      // Form validation failed, do nothing
    }
  };

  return (
    <>
      <style>{`
        /* ——— Luxe Popup Modal ——— */
        .popup-lux .ant-modal-content{
          background: linear-gradient(180deg, #ffffff, #f9fafb);
          border:1px solid rgba(251,191,36,0.35);
          border-radius:16px;
          box-shadow: 0 12px 28px rgba(17,24,39,0.12), 0 8px 16px rgba(17,24,39,0.08);
        }
        .popup-lux .ant-modal-header{ background: transparent; border-bottom:1px solid rgba(251,191,36,0.35); padding: 12px 16px; }
        .popup-lux .popup-head{ display:flex; align-items:center; gap:12px; }
        .popup-lux .popup-head .name{ font-weight:700; font-size:16px; letter-spacing:.2px; color:#111827; }
        .popup-lux .popup-head .meta{ font-size:12px; color:#6b7280; }
        .popup-lux .ant-form-item-label > label{ color:#1f2937; font-weight:600; }
        .popup-lux .ant-input, .popup-lux .ant-select-selector{ background:#fff; border:1px solid rgba(251,191,36,0.35); }
        .popup-lux .ant-input:focus, .popup-lux .ant-select-focused .ant-select-selector{ box-shadow: 0 0 0 3px rgba(251,191,36,0.25); border-color: rgba(251,191,36,0.65) !important; }
        .popup-lux .popup-footer{ position: sticky; bottom: -12px; background: linear-gradient(180deg, rgba(253,224,71,0.08), rgba(253,224,71,0.02)); border-top:1px solid rgba(251,191,36,0.35); padding: 12px 0; display:flex; justify-content:flex-end; gap:8px; margin-top: 4px; }
        /* Pill-shaped footer buttons with gold sweep hover */
        .popup-lux .popup-footer .btn-gold,
        .popup-lux .popup-footer .btn-ghost{
          border-radius: 999px;
          padding: 8px 14px;
          position: relative;
          overflow: hidden;
        }
        .popup-lux .popup-footer .btn-gold::after,
        .popup-lux .popup-footer .btn-ghost::after{
          content: "";
          position: absolute;
          inset: 0;
          transform: translateX(-120%);
          background: linear-gradient(120deg, rgba(255,255,255,0.00) 0%, rgba(255,255,255,0.45) 35%, rgba(255,255,255,0.00) 70%);
          transition: transform .65s ease;
          pointer-events: none;
        }
        .popup-lux .popup-footer .btn-gold:hover::after,
        .popup-lux .popup-footer .btn-ghost:hover::after{
          transform: translateX(120%);
        }
        /* Slightly richer ghost look in footer for contrast */
        .popup-lux .popup-footer .btn-ghost{
          color:#0b0b12;
          background: linear-gradient(180deg, rgba(253,224,71,0.12), rgba(253,224,71,0.04));
          border:1px solid rgba(251,191,36,0.35);
        }
        /* Focus-visible accessibility */
        .popup-lux .popup-footer .btn-gold:focus-visible,
        .popup-lux .popup-footer .btn-ghost:focus-visible{
          outline: none;
          box-shadow: 0 0 0 3px rgba(251,191,36,0.28);
        }
        .popup-lux .btn-gold{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); border:1px solid rgba(251,191,36,0.55); color:#0b0b12; box-shadow: inset 0 1px 0 rgba(255,255,255,0.35), 0 10px 22px rgba(251,191,36,0.18); }
        .popup-lux .btn-gold:hover{ filter:saturate(1.06) brightness(1.05); box-shadow: 0 12px 26px rgba(251,191,36,0.24); }
        .popup-lux .btn-ghost{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); border:1px solid rgba(251,191,36,0.28); color:#111827; }
        .popup-lux .status-active{ background: linear-gradient(180deg, rgba(253,224,71,0.96), rgba(234,179,8,0.96)); border:1px solid rgba(251,191,36,0.55); color:#0b0b12; }
        .popup-lux .status-inactive{ background: linear-gradient(180deg, rgba(148,163,184,0.35), rgba(30,41,59,0.35)); border:1px solid rgba(148,163,184,0.45); color:#111827; }
        @media (max-width: 767px){
          .popup-lux .ant-modal-content{ border-radius:0; height: 100vh; }
          .popup-lux .ant-modal-body{ padding: 12px; }
        }
        /* Popup summary chips - gold pill style with strong border, glow, dark text */
        .popup-lux .chips{ display:flex; gap:8px; flex-wrap:wrap; }
        .popup-lux .chip{
          background: rgba(253, 224, 71, 0.2);
          border: 1px solid rgba(253, 224, 71, 0.65);
          color: #fff;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 13px;
          box-shadow: 0 0 6px rgba(253, 224, 71, 0.4);
          backdrop-filter: blur(4px);
          font-weight: 500;
          letter-spacing: 0.01em;
          display: inline-flex;
          align-items: center;
        }

        /* Luxe table inside popup */
        /* ---- Luxe Close Button in Popup ---- */
        .popup-close-btn,
        .popup-lux .ant-modal-close,
        .popup-lux .ant-modal-close .ant-modal-close-x {
          color: #fff9f0 !important;
          text-shadow: 0 1px 2px rgba(0,0,0,0.6);
        }
        .popup-close-btn:hover,
        .popup-lux .ant-modal-close:hover,
        .popup-lux .ant-modal-close:active,
        .popup-lux .ant-modal-close:focus {
          color: #fff9f0 !important;
        }
        /* Ensure button background remains luxe */
        .popup-lux .ant-modal-close {
          background: linear-gradient(180deg, rgba(253,224,71,0.16), rgba(253,224,71,0.06));
          border-radius:10px;
          border: 1px solid rgba(253,224,71,0.35);
        }
        .popup-table-lux .ant-table{
          background: linear-gradient(180deg, #ffffff, #f9fafb);
          border:1px solid rgba(251,191,36,0.45);
          border-radius:12px; overflow:hidden;
        }
        .popup-table-lux .ant-table-thead > tr > th{
          background: linear-gradient(180deg, rgba(253,224,71,0.20), rgba(253,224,71,0.06));
          color:#111827; border-bottom:1px solid rgba(251,191,36,0.45);
          padding: 8px 10px;
        }
        .popup-table-lux .ant-table-tbody > tr > td{ padding:8px 10px; color:#1f2937; }
        .popup-table-lux .ant-table-tbody > tr:nth-child(even) > td{ background: rgba(253,224,71,0.06); }
        .popup-table-lux .ant-table-tbody > tr:hover > td{ background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.03)); }

        /* Popup-scoped dark luxe table overrides */
        .popup-lux .popup-table-lux .ant-table{
          background: linear-gradient(180deg, rgba(2,6,23,0.82), rgba(2,6,23,0.70));
          border:1px solid rgba(253,224,71,0.35);
          border-radius:12px; overflow:hidden;
        }
        .popup-lux .popup-table-lux .ant-table-thead > tr > th{
          background: linear-gradient(180deg, rgba(253,224,71,0.18), rgba(253,224,71,0.08));
          color:#f8fafc; border-bottom:1px solid rgba(253,224,71,0.40);
          padding: 8px 10px;
        }
        .popup-lux .popup-table-lux .ant-table-tbody > tr > td{ padding:8px 10px; color:#f8fafc; }
        .popup-lux .popup-table-lux .ant-table-tbody > tr:nth-child(even) > td{ background: rgba(253,224,71,0.06); }
        .popup-lux .popup-table-lux .ant-table-tbody > tr:hover > td{
          background: linear-gradient(180deg, rgba(253,224,71,0.12), rgba(253,224,71,0.04));
        }
        .popup-lux .popup-table-lux .ant-table-column-sorter, 
        .popup-lux .popup-table-lux .ant-table-column-sorter-inner{ color:#f8fafc; }

        /* --- Popup luxe enhancements (desktop & mobile) --- */
        /* Make section headers (e.g., Recent Activity) visible on dark modal */
        .popup-lux h1,
        .popup-lux h2,
        .popup-lux h3,
        .popup-lux .section-title{
          color:#f8fafc !important;
          text-shadow: 0 1px 0 rgba(0,0,0,0.35);
          background: linear-gradient(180deg, rgba(253,224,71,0.10), rgba(253,224,71,0.04));
          display: inline-block;
          padding: 6px 12px;
          border-radius: 10px;
          border: 1px solid rgba(253,224,71,0.35);
        }
        /* Ensure buttons remain readable on dark overlays */
        .popup-lux .ant-btn{
          font-weight: 600;
        }
        .popup-lux .ant-btn.btn-ghost{ color:#0b0b12; }
        .popup-lux .ant-btn.btn-gold{ color:#0b0b12; }
        .popup-lux .ant-modal-content{
          background:
            radial-gradient(900px 300px at 0% -10%, rgba(253,224,71,0.18), rgba(253,224,71,0) 60%),
            linear-gradient(180deg, #ffffff, #f9fafb);
          border:1px solid rgba(251,191,36,0.45);
          border-radius:16px;
          box-shadow: 0 18px 36px rgba(17,24,39,0.14), 0 6px 18px rgba(251,191,36,0.12);
        }
        .popup-lux .ant-modal-header{
          background: linear-gradient(180deg, rgba(253,224,71,0.14), rgba(253,224,71,0.06));
          border-bottom:1px solid rgba(251,191,36,0.45);
          position: sticky; top:0; z-index: 5;
        }
        /* Dark luxe variant: deep panel with gold glow */
        .popup-lux .ant-modal-content{
          background:
            radial-gradient(1000px 320px at 0% -10%, rgba(253,224,71,0.10), rgba(253,224,71,0) 60%),
            linear-gradient(180deg, rgba(2,6,23,0.94), rgba(2,6,23,0.88));
          border:1px solid rgba(253,224,71,0.35);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 18px 36px rgba(2,6,23,0.60), 0 8px 18px rgba(251,191,36,0.10);
        }
        .popup-lux .ant-modal-header{
          background: linear-gradient(180deg, rgba(253,224,71,0.18), rgba(253,224,71,0.08));
          border-bottom:1px solid rgba(253,224,71,0.40);
        }
        .popup-lux .ant-modal-body{
          background: linear-gradient(180deg, rgba(2,6,23,0.20), rgba(2,6,23,0.10));
        }
        .popup-lux .popup-head .meta{ color:#cbd5e1; }
        .popup-lux .ant-form-item-label > label{ color:#e5e7eb; }
        .popup-lux .ant-modal-close{
          width:32px; height:32px; display:flex; align-items:center; justify-content:center;
          right: 8px; top: 8px;
        }
        .popup-lux .ant-modal-close:hover{ filter: brightness(1.05) saturate(1.06); box-shadow: 0 6px 16px rgba(251,191,36,0.25); }
        .popup-lux .popup-head .name{
          background-image: linear-gradient(92deg,#111827 0%, #a16207 40%, #111827 100%);
          -webkit-background-clip:text; background-clip:text; color: transparent;
        }

        /* High-contrast luxe username for readability on dark header */
        .popup-lux .popup-head .name{
          color:#f8fafc !important;
          background-image:none !important;
          -webkit-background-clip:initial !important;
          background-clip:initial !important;
          text-shadow: 0 1px 2px rgba(0,0,0,0.6), 0 0 12px rgba(253,224,71,0.25);
          font-weight:800;
        }
        @media (min-width: 768px){
          .popup-lux .popup-head .name{ font-size:18px; }
        }
        @media (max-width: 767px){
          .popup-lux .popup-head .name{ font-size:16px; }
        }
        .popup-lux .ant-modal-body{
          background: linear-gradient(180deg, rgba(253,224,71,0.05), rgba(253,224,71,0.00));
        }
        @media (max-width: 767px){
          .popup-lux .ant-modal-content{ min-height: 100vh; border-radius: 0; }
          .popup-lux .ant-modal-header{ position: sticky; top:0; }
          .popup-lux .ant-modal-body{ padding-bottom: max(12px, env(safe-area-inset-bottom)); }
          .popup-lux .popup-footer{ position: sticky; bottom: 0; }
        }
        /* Compact mobile tuning */
        @media (max-width: 767px){
          .popup-lux .ant-modal-header{ padding: 8px 12px; }
          .popup-lux .ant-modal-body{ padding: 8px 10px; }
          .popup-lux .popup-head .name{ font-size: 15px; }
          .popup-lux .popup-head .meta{ font-size: 11px; }
          .popup-lux .chips{ gap:6px; }
          .popup-lux .chip{
            padding: 4px 8px;
            font-size: 11px;
          }
          .popup-lux .popup-footer{ padding: 8px 0; }
          .popup-table-lux .ant-table-thead > tr > th{ padding: 6px 8px; font-size: 12px; }
          .popup-table-lux .ant-table-tbody > tr > td{ padding: 6px 8px; font-size: 12px; }
          .popup-lux .ant-form-item{ margin-bottom: 8px; }
          .popup-lux .ant-form-item-label > label{ font-size: 12px; }
          .popup-lux .ant-input, .popup-lux .ant-select-selector{ padding: 4px 8px; }
        }
      `}</style>

      <Modal
        className="popup-lux"
        title={
          <div className="popup-head">
            <Avatar size={32} style={{ backgroundColor: '#fde68a', color: '#111827' }}>
              {initialChar(personData?.FullName, personData?.Username)}
            </Avatar>
            <div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div className="name">{personData?.Username || 'New User'}</div>
                <Tag
                  className={personData?.IsActive ? 'status-active' : 'status-inactive'}
                  style={{ marginLeft: 8 }}
                >
                  {personData?.IsActive ? 'Active' : 'Inactive'}
                </Tag>
              </div>
              <div className="meta">{personData?.FullName || ''}</div>
            </div>
            {/* Removed right-aligned Tag */}
          </div>
        }
        visible={visible}
        onCancel={onClose}
        footer={null}
        centered
        wrapClassName="popup-wrap-lux"
        width={isMobile ? '100%' : 840}
        style={{ top: isMobile ? 0 : 50, padding: isMobile ? 0 : undefined }}
        bodyStyle={{ padding: isMobile ? '12px 12px' : 16 }}
      >
        <div ref={scrollContainerRef} style={{ maxHeight: isMobile ? 'calc(100vh - 170px)' : '70vh', overflowY: 'auto', paddingRight: 8 }}>
          {!focusActivityOnOpen ? (
            <>
              <Form form={form} layout="vertical" initialValues={personData}>
                <Row gutter={[12,12]}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Username" name="Username">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Full Name" name="FullName">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Email" name="Email">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Role ID" name="RoleId">
                      <Select>
                        {roles.map((role) => (
                          <Option key={role.id} value={role.id}>
                            {role.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Is Active" name="IsActive" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Password Change Required" name="RequiresPasswordChange" valuePropName="checked">
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Failed Login Attempts" name="FailedLoginAttempts">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="User ID" name="UserID">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Last Login Date" name="LastLoginDate">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Password Last Changed" name="PasswordLastChanged">
                      <Input disabled />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>

              <div className="popup-footer">
                <Button className="btn-ghost" onClick={onClose}>Cancel</Button>
                <Button className="btn-gold" onClick={handleOk}>Save</Button>
              </div>
            </>
          ) : (
            <>
              <div className="chips" style={{ marginBottom: 8 }}>
                <span
                  className="chip"
                  style={{
                    background: 'rgba(253, 224, 71, 0.2)',
                    border: '1px solid rgba(253, 224, 71, 0.65)',
                    color: '#fff',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    boxShadow: '0 0 6px rgba(253, 224, 71, 0.4)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 500,
                  }}
                >
                  Role: {roles.find(r => r.id === personData?.RoleId)?.name || personData?.RoleId || '-'}
                </span>
                <span
                  className="chip"
                  style={{
                    background: 'rgba(253, 224, 71, 0.2)',
                    border: '1px solid rgba(253, 224, 71, 0.65)',
                    color: '#fff',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    boxShadow: '0 0 6px rgba(253, 224, 71, 0.4)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 500,
                  }}
                >
                  Created: {formatDateSA(personData?.CreatedAt)}
                </span>
                <span
                  className="chip"
                  style={{
                    background: 'rgba(253, 224, 71, 0.2)',
                    border: '1px solid rgba(253, 224, 71, 0.65)',
                    color: '#fff',
                    borderRadius: '999px',
                    padding: '6px 12px',
                    boxShadow: '0 0 6px rgba(253, 224, 71, 0.4)',
                    backdropFilter: 'blur(4px)',
                    fontWeight: 500,
                  }}
                >
                  Last seen: {formatDateSA(personData?.LastLoginDate)}
                </span>
                {typeof personData?.FailedLoginAttempts === 'number' ? (
                  <span
                    className="chip"
                    style={{
                      background: 'rgba(253, 224, 71, 0.2)',
                      border: '1px solid rgba(253, 224, 71, 0.65)',
                      color: '#fff',
                      borderRadius: '999px',
                      padding: '6px 12px',
                      boxShadow: '0 0 6px rgba(253, 224, 71, 0.4)',
                      backdropFilter: 'blur(4px)',
                      fontWeight: 500,
                    }}
                  >
                    Failed logins: {personData.FailedLoginAttempts}
                  </span>
                ) : null}
              </div>
              <div className="popup-footer">
                <Button className="btn-ghost" onClick={onClose}>Close</Button>
              </div>
            </>
          )}
          <h3 ref={activityRef} style={{ marginTop: '1rem' }}>Recent Activity</h3>
          <Table
            className="popup-table-lux"
            columns={[
              { title: '#', key: 'rowNumber', width: 56, align: 'center', render: (_v, _r, idx) => idx + 1 },
              { title: 'Time', dataIndex: 'ActionTime', key: 'ActionTime', render: (v) => formatDateSA(v) },
              { title: 'Type', dataIndex: 'ResultType', key: 'ResultType' },
              { title: 'Action', dataIndex: 'Action', key: 'Action' },
              { title: 'By', dataIndex: 'PerformedBy', key: 'PerformedBy' },
              { title: 'Details', dataIndex: 'Details', key: 'Details' },
            ]}
            dataSource={filteredLogsDesc}
            pagination={false}
            rowKey="LogRef"
            size="small"
          />
        </div>
      </Modal>
    </>
  );
}
